import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useFavoritedMattersContextOptional } from '@/contexts/FavoritedMattersContext';
import { cn } from '@/lib/utils';

/**
 * Default star icon size class - use this for consistency across all star icons.
 * h-5 w-5 = 20px, ~25% larger than h-4 for better click targets.
 * Can be overridden when needed, but prefer using the default.
 */
export const STAR_ICON_SIZE = 'h-5 w-5';

/** Animation duration in milliseconds for the favorite pop effect */
const ANIMATION_DURATION_MS = 400;

type FavoriteButtonSize = 'xs' | 'sm' | 'md';

interface FavoriteButtonProps {
  /** The matter ID to favorite/unfavorite */
  matterId: string;
  /** The matter name (for display in toasts and storage) */
  matterName: string;
  /** Size variant of the button */
  size?: FavoriteButtonSize;
  /** Whether to show toast notifications */
  showToast?: boolean;
  /** Additional class names */
  className?: string;
  /** Callback when favorite status changes */
  onToggle?: (isFavorited: boolean) => void;
}

const sizeConfig: Record<FavoriteButtonSize, { icon: string; button: string }> =
  {
    xs: {
      icon: STAR_ICON_SIZE,
      button: 'p-0.5',
    },
    sm: {
      icon: STAR_ICON_SIZE,
      button: 'p-1',
    },
    md: {
      icon: 'h-6 w-6',
      button: 'p-1.5',
    },
  };

/** Shared animation config for the pop effect */
const popAnimation = {
  scale: [1, 1.25, 1],
  transition: {
    duration: 0.35,
    times: [0, 0.4, 1],
    ease: 'easeOut' as const,
  },
};

interface UseFavoriteButtonOptions {
  matterId: string;
  matterName: string;
  showToast: boolean;
  onToggle?: (isFavorited: boolean) => void;
}

/**
 * Shared hook for favorite button logic.
 * Handles context access, click handling, animation state, and toast notifications.
 */
const useFavoriteButton = ({
  matterId,
  matterName,
  showToast,
  onToggle,
}: UseFavoriteButtonOptions) => {
  const context = useFavoritedMattersContextOptional();
  const [isAnimating, setIsAnimating] = useState(false);

  const isFavorited = context?.isFavorited(matterId) ?? false;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!context) return;

      const wasFavorited = context.isFavorited(matterId);

      // Trigger animation only when favoriting (not unfavoriting)
      if (!wasFavorited) {
        setIsAnimating(true);
      }

      context.toggleFavorite(matterId, matterName);

      if (showToast) {
        toast.success(
          wasFavorited ? 'Removed from favorites' : 'Added to favorites',
        );
      }

      onToggle?.(!wasFavorited);
    },
    [context, matterId, matterName, showToast, onToggle],
  );

  // Reset animation state after animation completes
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(
        () => setIsAnimating(false),
        ANIMATION_DURATION_MS,
      );
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isAnimating]);

  const IconComponent = isFavorited ? StarIconSolid : StarIconOutline;

  return {
    context,
    isFavorited,
    isAnimating,
    handleClick,
    IconComponent,
  };
};

/**
 * A button component for favoriting/unfavoriting matters.
 * Displays a star icon that fills when favorited with a subtle spring pop animation.
 *
 * Must be used within a FavoritedMattersProvider, or will render nothing.
 */
export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  matterId,
  matterName,
  size = 'sm',
  showToast = true,
  className,
  onToggle,
}) => {
  const { context, isFavorited, isAnimating, handleClick, IconComponent } =
    useFavoriteButton({ matterId, matterName, showToast, onToggle });

  // Don't render if no context available
  if (!context) {
    return null;
  }

  const { icon: iconSize, button: buttonSize } = sizeConfig[size];

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      whileTap={{ scale: 0.85 }}
      animate={isAnimating ? popAnimation : { scale: 1 }}
      className={cn(
        'rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-1',
        buttonSize,
        isFavorited
          ? 'text-amber-400 hover:text-amber-500'
          : 'text-zinc-400 hover:text-amber-400',
        className,
      )}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorited}
    >
      <IconComponent className={iconSize} aria-hidden="true" />
    </motion.button>
  );
};

/**
 * A more minimal version for use in tight spaces like table cells.
 * Shows as a subtle icon that becomes visible on hover.
 * Includes subtle spring pop animation when favoriting.
 */
export const FavoriteButtonCompact: React.FC<
  Omit<FavoriteButtonProps, 'size'>
> = ({ matterId, matterName, showToast = false, className, onToggle }) => {
  const { context, isFavorited, isAnimating, handleClick, IconComponent } =
    useFavoriteButton({ matterId, matterName, showToast, onToggle });

  if (!context) {
    return null;
  }

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      whileTap={{ scale: 0.85 }}
      animate={isAnimating ? popAnimation : { scale: 1 }}
      className={cn(
        'inline-flex items-center justify-center rounded cursor-pointer',
        isFavorited
          ? 'text-amber-400'
          : 'text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-amber-400',
        className,
      )}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <IconComponent
        className={cn(STAR_ICON_SIZE, 'shrink-0')}
        aria-hidden="true"
      />
    </motion.button>
  );
};
